import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { brl } from "../lib";
import { toastSuccess, toastError, pickErrorMsg } from "../lib/feedback";
import { CATALOGO_SERVICOS, UNIDADE_POR_DESCRICAO } from "./servicosCatalogo";
import { AlertTriangle } from "lucide-react";
import { RetrabalhoBox, SaldoBloco, ReciboConsolidadoBox, ReciboRetrabalhos } from "./ServicosRetrabalho";
import { PagamentoEditRow, BtnEditPagamento } from "./PagamentoEditRow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --- estilos base (seguem paleta do Fretes/Signature Book) ---
const ink = "#f5f0e8";
const ink2 = "#cdc8be";
const ink3 = "#8a857a";
const gold = "#b8935a";
const line = "#2b2833";
const bg2 = "#15141a";
const bg1 = "#0d0d0f";

const lbl = { display: "block", fontSize: 10, color: ink3, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 5 };
const inp = {
  width: "100%", background: bg1, border: `1px solid ${line}`,
  color: ink, padding: "8px 10px", fontSize: 13, borderRadius: 3, boxSizing: "border-box",
};
const btnGhost = {
  background: "transparent", color: ink2, border: `1px solid ${line}`,
  padding: "8px 16px", fontSize: 12, letterSpacing: 0.5, cursor: "pointer",
};
const btnPrimary = {
  background: gold, color: bg1, border: "none",
  padding: "9px 20px", fontSize: 12, fontWeight: 600, letterSpacing: 0.5, cursor: "pointer",
};
const btnDanger = { ...btnGhost, color: "#c47a7a", borderColor: "rgba(139,58,58,0.5)" };

const TIPO_LABEL = { colocacao: "Colocação", frete: "Frete", medicao: "Medição" };

// Adiantamento entra no bloco da visita da mesma data; senão, na visita anterior
// mais próxima; se for anterior a todas, na primeira visita. (Mesma regra do backend.)
function blocoDoAdiantamento(datasSet, a) {
  const datas = [...(datasSet || [])].filter(Boolean).sort();
  if (!datas.length) return datasSet && datasSet.has("") ? "" : (a.data || "");
  const ad = a.data || String(a.created_at || "").slice(0, 10);
  const antes = datas.filter(d => d <= ad);
  return antes.length ? antes[antes.length - 1] : datas[0];
}

function tipoBadgeStyle(tipo) {
  const color = { colocacao: "#b8935a", frete: "#5fa86c", medicao: "#8a9fbc" }[tipo] || ink3;
  return {
    fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase",
    color, border: `1px solid ${color}`, padding: "2px 8px", borderRadius: 2,
  };
}

function fmtDataBR(iso) {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  return `${d}/${m}/${y}`;
}

/**
 * Alerta inline mostrado antes do pagamento de um terceiro/prestador quando
 * existem sinistros com saldo_devedor > 0 vinculados àquele nome.
 * (2026-02-19: propagação Ocorrências → Serviços)
 */
function SinistrosPendentesAlert({ nome, obraId, headers }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!nome) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/ocorrencias/pendentes/${encodeURIComponent(nome)}`,
          { headers, params: { obra_id: obraId } },
        );
        if (!cancelled) setData(r.data);
      } catch { /* silencioso — não bloqueia UI de serviços */ }
    })();
    return () => { cancelled = true; };
  }, [nome, obraId, headers]);
  if (!data || !data.total_pendente || data.total_pendente <= 0) return null;
  return (
    <div
      style={{
        background: "rgba(196,122,122,0.08)",
        border: "1px solid rgba(196,122,122,0.4)",
        borderRadius: 3, padding: "10px 14px",
        display: "flex", alignItems: "center", gap: 10,
        margin: "6px 0 12px",
      }}
      data-testid={`alerta-sinistros-${nome}`}
    >
      <AlertTriangle size={16} color="#c47a7a" style={{ flexShrink: 0 }} />
      <div style={{ fontSize: 12, color: "#e5b8b8", lineHeight: 1.5, flex: 1 }}>
        <strong>{brl(data.total_pendente)}</strong> em sinistros pendentes para <strong>{nome}</strong>.
        Considere descontar do próximo pagamento (aba <em>Ocorrências</em> desta obra).
      </div>
      <span style={{
        fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase",
        color: "#c47a7a", padding: "3px 8px", border: "1px solid rgba(196,122,122,0.4)",
        borderRadius: 2,
      }}>
        {data.sinistros.length} sinistro(s)
      </span>
    </div>
  );
}

/**
 * Aba "Serviços" dentro do detalhe da obra.
 * Admin/gestor/financeiro (verificado no backend) podem registrar Colocações, Fretes
 * e Medições vinculados a um prestador ou terceirizado, com adiantamentos e recibo.
 */
export default function ServicosPanel({ obraId, userRole }) {
  const podeRetrab = ["admin", "gestor"].includes(userRole);
  const [showConsolidado, setShowConsolidado] = useState(false);
  const nav = useNavigate();
  const [data, setData] = useState({ servicos: [], adiantamentos: [], total: 0, total_adiantamentos: 0, saldo: 0 });
  const [prestadores, setPrestadores] = useState([]);
  const [terceirizados, setTerceirizados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showAdiantForm, setShowAdiantForm] = useState(false);
  const [editing, setEditing] = useState(null); // servico em edição
  const editFormRef = useRef(null);

  useEffect(() => {
    if (editing && editFormRef.current) {
      // aguarda o form montar antes de rolar
      setTimeout(() => {
        editFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }
  }, [editing]);
  const [linkModal, setLinkModal] = useState(null); // { public_token, prestador_nome }
  const [gerandoLink, setGerandoLink] = useState(false);
  const [recibos, setRecibos] = useState([]);
  const [histFiltroPrest, setHistFiltroPrest] = useState("todos");
  const [histFiltroStatus, setHistFiltroStatus] = useState("todos");

  const [form, setForm] = useState({
    tipo: "colocacao",
    prestador_ref: "", // "prestador:id" ou "terceirizado:id"
    descricao: "",
    unidade: "",
    quantidade: 1,
    valor_unitario: "",
    data: new Date().toISOString().slice(0, 10),
    observacoes: "",
  });
  // Modo multi-linha (nota fiscal): array de itens editáveis antes de salvar.
  // Só é usado em MODO CRIAÇÃO. Em edição, usa `form` clássico.
  const emptyLinha = () => ({
    tipo: "colocacao",
    prestador_ref: "",
    descricao: "",
    unidade: "",
    quantidade: 1,
    valor_unitario: "",
    data: new Date().toISOString().slice(0, 10),
    observacoes: "",
  });
  const [linhas, setLinhas] = useState([emptyLinha()]);
  const [salvandoBulk, setSalvandoBulk] = useState(false);
  // Edição em grupo: reabre as linhas já salvas de um prestador + novas linhas
  const [grupoEdit, setGrupoEdit] = useState(null); // { key, nome }
  const [removidos, setRemovidos] = useState([]); // ids de linhas salvas a excluir
  const bulkFormRef = useRef(null);
  useEffect(() => {
    if (grupoEdit) setTimeout(() => bulkFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  }, [grupoEdit]);
  const [adiantForm, setAdiantForm] = useState({
    prestador_ref: "",
    valor: "",
    data: new Date().toISOString().slice(0, 10),
    descricao: "",
  });

  const token = localStorage.getItem("cps_token");
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const [srv, prest, terc, recs] = await Promise.all([
        axios.get(`${API}/obras/${obraId}/servicos`, { headers }),
        axios.get(`${API}/prestadores`, { headers }),
        axios.get(`${API}/terceirizados`, { headers }),
        axios.get(`${API}/obras/${obraId}/servicos/recibos`, { headers }),
      ]);
      setData(srv.data);
      setPrestadores(prest.data || []);
      setTerceirizados(terc.data || []);
      setRecibos(recs.data || []);
    } catch (e) {
      console.error("[Servicos] load:", e);
    } finally {
      setLoading(false);
    }
  }, [obraId]);

  useEffect(() => { load(); }, [load]);

  // Opções de dropdown: prestadores + terceirizados unificados
  const opcoesPrestador = useMemo(() => {
    const p = (prestadores || []).filter(x => x.status !== "inativo")
      .map(x => ({ ref: `prestador:${x.id}`, label: `${x.nome}${x.especialidade ? ` · ${x.especialidade}` : ""}`, group: "Prestadores" }));
    const t = (terceirizados || []).filter(x => x.status !== "inativo")
      .map(x => ({ ref: `terceirizado:${x.id}`, label: `${x.nome}${x.tipo ? ` · ${x.tipo}` : ""}`, group: "Terceirizados" }));
    return [...p, ...t];
  }, [prestadores, terceirizados]);

  const parseRef = (ref) => {
    if (!ref) return { kind: null, id: null };
    const [kind, id] = ref.split(":");
    return { kind, id };
  };

  const resetForm = () => {
    setEditing(null);
    setForm({
      tipo: "colocacao",
      prestador_ref: "",
      descricao: "",
      unidade: "",
      quantidade: 1,
      valor_unitario: "",
      data: new Date().toISOString().slice(0, 10),
      observacoes: "",
    });
    setLinhas([emptyLinha()]);
    setGrupoEdit(null);
    setRemovidos([]);
  };

  const linhaDeServico = (sv) => ({
    id: sv.id,
    tipo: sv.tipo,
    prestador_ref: `${sv.prestador_kind}:${sv.prestador_id}`,
    descricao: sv.descricao || "",
    unidade: sv.unidade || "",
    quantidade: sv.quantidade || 1,
    valor_unitario: sv.valor_unitario ?? "",
    data: sv.data || new Date().toISOString().slice(0, 10),
    observacoes: sv.observacoes || "",
  });

  const startGrupoEdit = (g) => {
    const salvas = [...g.servicos]
      .sort((a, b) => String(a.data || "").localeCompare(String(b.data || "")))
      .map(linhaDeServico);
    const last = salvas[salvas.length - 1];
    setEditing(null);
    setShowAdiantForm(false);
    setRemovidos([]);
    setLinhas([...salvas, { ...emptyLinha(), prestador_ref: g.ref, tipo: last?.tipo || "colocacao", data: g.data || emptyLinha().data }]);
    setGrupoEdit({ key: g.key, nome: g.data ? `${g.nome} — ${fmtDataBR(g.data)}` : g.nome });
    setShowForm(true);
  };

  const updateLinha = (idx, patch) => {
    setLinhas(prev => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));
  };
  const addLinha = () => {
    setLinhas(prev => {
      const last = prev[prev.length - 1] || emptyLinha();
      // Nova linha herda tipo/prestador/data da anterior — acelera lançamento em série
      return [...prev, {
        ...emptyLinha(),
        tipo: last.tipo,
        prestador_ref: last.prestador_ref,
        data: last.data,
      }];
    });
  };
  const removeLinha = (idx) => {
    const alvo = linhas[idx];
    if (alvo?.id) setRemovidos(prev => [...prev, alvo.id]);
    setLinhas(prev => prev.length <= 1 ? [emptyLinha()] : prev.filter((_, i) => i !== idx));
  };
  const totalBulk = useMemo(
    () => linhas.reduce((s, l) => s + (Number(l.quantidade) || 0) * (Number(l.valor_unitario) || 0), 0),
    [linhas],
  );

  const startEdit = (s) => {
    setEditing(s);
    setForm({
      tipo: s.tipo,
      prestador_ref: `${s.prestador_kind}:${s.prestador_id}`,
      descricao: s.descricao || "",
      unidade: s.unidade || "",
      quantidade: s.quantidade || 1,
      valor_unitario: s.valor_unitario || "",
      data: s.data || new Date().toISOString().slice(0, 10),
      observacoes: s.observacoes || "",
    });
    setShowForm(true);
  };

  const salvar = async () => {
    const { kind, id } = parseRef(form.prestador_ref);
    if (!kind || !id) return alert("Selecione um prestador/terceirizado.");
    if (!form.descricao.trim()) return alert("Descreva o serviço.");
    const qtd = Number(form.quantidade);
    const vu = Number(form.valor_unitario);
    if (!(qtd > 0)) return alert("Quantidade deve ser > 0.");
    if (!(vu >= 0)) return alert("Valor unitário inválido.");
    const body = {
      tipo: form.tipo,
      prestador_kind: kind,
      prestador_id: id,
      descricao: form.descricao.trim(),
      unidade: (form.unidade || "").trim() || null,
      quantidade: qtd,
      valor_unitario: vu,
      data: form.data || null,
      observacoes: form.observacoes.trim() || null,
    };
    try {
      if (editing) {
        await axios.patch(`${API}/obras/${obraId}/servicos/${editing.id}`, body, { headers });
      } else {
        await axios.post(`${API}/obras/${obraId}/servicos`, body, { headers });
      }
      setShowForm(false);
      resetForm();
      await load();
    } catch (e) {
      alert("Erro: " + (e?.response?.data?.detail || e.message));
    }
  };

  // Salva várias linhas em UMA chamada atômica (POST /servicos/bulk)
  const salvarBulk = async () => {
    // Filtra linhas realmente preenchidas (descrição + valor). Ignora vazias.
    const items = [];
    for (let i = 0; i < linhas.length; i++) {
      const l = linhas[i];
      const desc = (l.descricao || "").trim();
      const qtd = Number(l.quantidade);
      const vu = Number(l.valor_unitario);
      const { kind, id } = parseRef(l.prestador_ref);
      const preenchida = l.id || desc || vu > 0;
      if (!preenchida) continue; // linha ainda em branco — pula
      if (!kind || !id) return alert(`Linha ${i + 1}: selecione o prestador/terceirizado.`);
      if (!desc) return alert(`Linha ${i + 1}: descreva o serviço.`);
      if (!(qtd > 0)) return alert(`Linha ${i + 1}: quantidade deve ser > 0.`);
      if (!(vu >= 0)) return alert(`Linha ${i + 1}: valor unitário inválido.`);
      items.push({
        id: l.id,
        tipo: l.tipo,
        prestador_kind: kind,
        prestador_id: id,
        descricao: desc,
        unidade: (l.unidade || "").trim() || null,
        quantidade: qtd,
        valor_unitario: vu,
        data: l.data || null,
        observacoes: (l.observacoes || "").trim() || null,
      });
    }
    if (items.length === 0 && removidos.length === 0) return alert("Preencha ao menos uma linha antes de salvar.");
    if (removidos.length && !window.confirm(`Excluir ${removidos.length} linha(s) já salva(s)?`)) return;

    const novos = items.filter(it => !it.id).map(({ id, ...rest }) => rest);
    const originais = Object.fromEntries((data.servicos || []).map(sv => [sv.id, sv]));
    const CAMPOS_CMP = ["tipo", "prestador_kind", "prestador_id", "descricao", "unidade", "quantidade", "valor_unitario", "data", "observacoes"];
    const alterados = items.filter(it => it.id && originais[it.id] &&
      CAMPOS_CMP.some(k => String(it[k] ?? "") !== String(originais[it.id][k] ?? "")));

    setSalvandoBulk(true);
    try {
      for (const { id, ...body } of alterados) {
        await axios.patch(`${API}/obras/${obraId}/servicos/${id}`, body, { headers });
      }
      for (const id of removidos) {
        await axios.delete(`${API}/obras/${obraId}/servicos/${id}`, { headers });
      }
      let criados = { count: 0, total: 0 };
      if (novos.length) {
        criados = (await axios.post(`${API}/obras/${obraId}/servicos/bulk`, { items: novos }, { headers })).data;
      }
      const eraGrupo = !!grupoEdit;
      setShowForm(false);
      resetForm();
      await load();
      if (eraGrupo) {
        toastSuccess(`Salvo: ${criados.count} nova(s), ${alterados.length} alterada(s), ${removidos.length} excluída(s)`);
      } else {
        alert(`${criados.count} serviço(s) registrado(s). Total: ${brl(criados.total)}`);
      }
    } catch (e) {
      alert("Erro: " + (e?.response?.data?.detail || e.message));
      await load();
    } finally {
      setSalvandoBulk(false);
    }
  };

  const remover = async (sid) => {
    if (!window.confirm("Remover este serviço?")) return;
    try {
      await axios.delete(`${API}/obras/${obraId}/servicos/${sid}`, { headers });
      await load();
    } catch (e) {
      alert("Erro: " + (e?.response?.data?.detail || e.message));
    }
  };

  const salvarAdiant = async () => {
    const { kind, id } = parseRef(adiantForm.prestador_ref);
    if (!kind || !id) return alert("Selecione um prestador/terceirizado.");
    const v = Number(adiantForm.valor);
    if (!(v > 0)) return alert("Valor deve ser > 0.");
    try {
      await axios.post(`${API}/obras/${obraId}/servicos/adiantamentos`, {
        prestador_kind: kind, prestador_id: id, valor: v,
        data: adiantForm.data || null,
        descricao: adiantForm.descricao.trim() || null,
      }, { headers });
      setShowAdiantForm(false);
      setAdiantForm({ prestador_ref: "", valor: "", data: new Date().toISOString().slice(0, 10), descricao: "" });
      await load();
    } catch (e) {
      alert("Erro: " + (e?.response?.data?.detail || e.message));
    }
  };

  const removerAdiant = async (aid) => {
    if (!window.confirm("Remover este adiantamento?")) return;
    try {
      await axios.delete(`${API}/obras/${obraId}/servicos/adiantamentos/${aid}`, { headers });
      await load();
    } catch (e) {
      alert("Erro: " + (e?.response?.data?.detail || e.message));
    }
  };

  // Agrupa por prestador + data (cada visita = um bloco, com recibo próprio)
  const gruposPorPrestador = useMemo(() => {
    const map = {};
    const datasPorRef = {};
    const novo = (ref, kind, id, nome, d) => ({ key: `${ref}:${d}`, ref, kind, id, nome, data: d, servicos: [], adiantamentos: [], retrabalhos: [], total: 0, total_adiant: 0, total_retrab: 0 });
    (data.servicos || []).forEach(s => {
      const ref = `${s.prestador_kind}:${s.prestador_id}`;
      const d = s.data || "";
      const k = `${ref}:${d}`;
      if (!map[k]) map[k] = novo(ref, s.prestador_kind, s.prestador_id, s.prestador_nome, d);
      map[k].servicos.push(s);
      map[k].total += Number(s.valor_total || 0);
      (datasPorRef[ref] = datasPorRef[ref] || new Set()).add(d);
    });
    (data.adiantamentos || []).forEach(a => {
      const ref = `${a.prestador_kind}:${a.prestador_id}`;
      const d = blocoDoAdiantamento(datasPorRef[ref], a);
      const k = `${ref}:${d}`;
      if (!map[k]) map[k] = novo(ref, a.prestador_kind, a.prestador_id, a.prestador_nome, d);
      map[k].adiantamentos.push(a);
      map[k].total_adiant += Number(a.valor || 0);
    });
    (data.retrabalhos || []).forEach(r => {
      const ref = `${r.prestador_kind}:${r.prestador_id}`;
      const k = `${ref}:${r.data || ""}`;
      const nome = (Object.values(map).find(x => x.ref === ref) || {}).nome || "";
      if (!map[k]) map[k] = novo(ref, r.prestador_kind, r.prestador_id, nome, r.data || "");
      map[k].retrabalhos.push(r);
      map[k].total_retrab += Number(r.valor || 0);
    });
    // Blocos do mesmo prestador juntos, datas em ordem decrescente; prestador com visita mais recente primeiro
    const grupos = Object.values(map);
    const recente = {};
    const resumo = {};
    grupos.forEach(g => {
      if ((g.data || "") > (recente[g.ref] || "")) recente[g.ref] = g.data || "";
      const r = (resumo[g.ref] = resumo[g.ref] || { total: 0, total_adiant: 0, total_retrab: 0 });
      r.total += g.total;
      r.total_adiant += g.total_adiant;
      r.total_retrab += g.total_retrab;
    });
    const ordenados = grupos.sort((a, b) =>
      (recente[b.ref] || "").localeCompare(recente[a.ref] || "")
      || String(a.nome || "").localeCompare(String(b.nome || ""))
      || a.ref.localeCompare(b.ref)
      || (b.data || "").localeCompare(a.data || ""));
    return ordenados.map((g, i) => ({
      ...g,
      primeiro: i === 0 || ordenados[i - 1].ref !== g.ref,
      resumo: i === ordenados.length - 1 || ordenados[i + 1].ref !== g.ref ? resumo[g.ref] : null,
    }));
  }, [data]);

  if (loading) return <div style={{ padding: 24, color: ink3, fontSize: 13 }}>Carregando serviços…</div>;

  return (
    <div data-testid="servicos-panel" style={{ padding: "8px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ margin: 0, fontFamily: "'Fraunces',serif", fontSize: 22, color: ink, fontWeight: 400 }}>
          Serviços da Obra
        </h3>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={() => setShowConsolidado(!showConsolidado)} data-testid="servicos-recibo-consolidado-btn" style={{ ...btnGhost, color: gold, borderColor: gold }}>
            Recibo Consolidado
          </button>
          <button onClick={() => { setShowAdiantForm(!showAdiantForm); setShowForm(false); resetForm(); }} data-testid="servicos-novo-adiant-btn" style={showAdiantForm ? btnGhost : { ...btnGhost, color: ink }}>
            {showAdiantForm ? "Fechar" : "+ Adiantamento"}
          </button>
          <button onClick={() => { setShowForm(!showForm); setShowAdiantForm(false); if (showForm) resetForm(); }} data-testid="servicos-novo-btn" style={showForm ? btnGhost : btnPrimary}>
            {showForm ? "Cancelar" : (editing ? "Editando…" : "+ Registrar Serviço")}
          </button>
        </div>
      </div>

      {/* Totais */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12, marginBottom: 20 }}>
        <KpiCard label="Total de Serviços" valor={brl(data.total)} testid="kpi-total" />
        <KpiCard label="Adiantamentos" valor={brl(data.total_adiantamentos)} testid="kpi-adiant" tone="muted" />
        {(podeRetrab || data.total_retrabalho > 0) && <KpiCard label="Retrabalho" valor={`− ${brl(data.total_retrabalho || 0)}`} testid="kpi-retrab" tone="muted" />}
        <KpiCard label="Saldo a Pagar" valor={brl(data.saldo)} testid="kpi-saldo" tone="gold" />
      </div>

      {showConsolidado && (
        <ReciboConsolidadoBox
          obraId={obraId}
          grupos={gruposPorPrestador}
          headers={headers}
          contatos={g => (g.kind === "prestador" ? prestadores : terceirizados).find(x => x.id === g.id)}
          onLink={setLinkModal}
          onClose={() => setShowConsolidado(false)}
        />
      )}

      {/* Form de serviço — modo EDIÇÃO (linha única) */}
      {showForm && editing && (
        <div ref={editFormRef} style={{ background: bg2, border: `1px solid ${line}`, borderRadius: 6, padding: 18, marginBottom: 18 }} data-testid="servicos-form">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={lbl}>Tipo</label>
              <select value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })} style={inp} data-testid="servicos-form-tipo">
                <option value="colocacao">Colocação</option>
                <option value="frete">Frete</option>
                <option value="medicao">Medição</option>
              </select>
            </div>
            <div>
              <label style={lbl}>Prestador / Terceirizado</label>
              <select value={form.prestador_ref} onChange={e => setForm({ ...form, prestador_ref: e.target.value })} style={inp} data-testid="servicos-form-prestador">
                <option value="">— Selecione —</option>
                <optgroup label="Prestadores">
                  {opcoesPrestador.filter(o => o.group === "Prestadores").map(o => (
                    <option key={o.ref} value={o.ref}>{o.label}</option>
                  ))}
                </optgroup>
                <optgroup label="Terceirizados">
                  {opcoesPrestador.filter(o => o.group === "Terceirizados").map(o => (
                    <option key={o.ref} value={o.ref}>{o.label}</option>
                  ))}
                </optgroup>
              </select>
            </div>
            <div style={{ gridColumn: "span 2" }}>
              <label style={lbl}>Descrição (digite ou escolha da lista)</label>
              <input
                list="cps-servicos-catalogo"
                value={form.descricao}
                onChange={e => {
                  const val = e.target.value;
                  const uniAuto = UNIDADE_POR_DESCRICAO[val];
                  setForm(f => ({
                    ...f,
                    descricao: val,
                    unidade: uniAuto ? uniAuto : f.unidade,
                  }));
                }}
                style={inp}
                placeholder="Ex: Colocação de bancada"
                data-testid="servicos-form-descricao"
              />
              <datalist id="cps-servicos-catalogo">
                {CATALOGO_SERVICOS.map(s => (
                  <option key={s.descricao} value={s.descricao}>{s.unidade}</option>
                ))}
              </datalist>
            </div>
            <div>
              <label style={lbl}>Unidade</label>
              <input value={form.unidade} onChange={e => setForm({ ...form, unidade: e.target.value })} style={inp} placeholder="m², ml, un., verba..." data-testid="servicos-form-unidade" />
            </div>
            <div>
              <label style={lbl}>Quantidade</label>
              <input type="number" step="0.01" min="0.01" value={form.quantidade} onChange={e => setForm({ ...form, quantidade: e.target.value })} style={inp} data-testid="servicos-form-qtd" />
            </div>
            <div>
              <label style={lbl}>Valor Unitário (R$)</label>
              <input type="number" step="0.01" min="0" value={form.valor_unitario} onChange={e => setForm({ ...form, valor_unitario: e.target.value })} style={inp} data-testid="servicos-form-vu" />
            </div>
            <div>
              <label style={lbl}>Data</label>
              <input type="date" value={form.data} onChange={e => setForm({ ...form, data: e.target.value })} style={inp} data-testid="servicos-form-data" />
            </div>
            <div>
              <label style={lbl}>Total calculado</label>
              <div style={{ ...inp, background: bg1, color: gold, fontWeight: 600 }}>
                {brl((Number(form.quantidade) || 0) * (Number(form.valor_unitario) || 0))}
              </div>
            </div>
            <div style={{ gridColumn: "span 2" }}>
              <label style={lbl}>Observações</label>
              <textarea value={form.observacoes} onChange={e => setForm({ ...form, observacoes: e.target.value })} style={{ ...inp, minHeight: 60 }} data-testid="servicos-form-obs" />
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
            <button onClick={() => { setShowForm(false); resetForm(); }} style={btnGhost}>Cancelar</button>
            <button onClick={salvar} style={btnPrimary} data-testid="servicos-form-save">Atualizar</button>
          </div>
        </div>
      )}

      {/* Form de serviço — modo CRIAÇÃO multi-linha (tipo NF) */}
      {showForm && !editing && (
        <div ref={bulkFormRef} style={{ background: bg2, border: `1px solid ${grupoEdit ? gold : line}`, borderRadius: 6, padding: 18, marginBottom: 18 }} data-testid="servicos-form-bulk">
          <datalist id="cps-servicos-catalogo">
            {CATALOGO_SERVICOS.map(s => (
              <option key={s.descricao} value={s.descricao}>{s.unidade}</option>
            ))}
          </datalist>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", color: grupoEdit ? gold : ink3 }} data-testid="bulk-form-title">
              {grupoEdit
                ? `${grupoEdit.nome} — editar e acrescentar linhas (${linhas.filter(l => l.id).length} salva(s) · ${linhas.filter(l => !l.id).length} nova(s))`
                : `Novos serviços (${linhas.length} linha${linhas.length > 1 ? "s" : ""})`}
            </div>
            <div style={{ fontSize: 11, color: ink3 }}>
              {grupoEdit ? "Altere as linhas salvas ou use + Linha para acrescentar" : "Dica: adicione todos os serviços da obra antes de salvar"}
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 900 }}>
              <thead>
                <tr style={{ textAlign: "left", color: ink3, fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase" }}>
                  <th style={thBulk}>#</th>
                  <th style={thBulk}>Tipo</th>
                  <th style={thBulk}>Prestador</th>
                  <th style={{ ...thBulk, minWidth: 220 }}>Descrição</th>
                  <th style={{ ...thBulk, width: 80 }}>Un.</th>
                  <th style={{ ...thBulk, width: 80, textAlign: "right" }}>Qtd</th>
                  <th style={{ ...thBulk, width: 110, textAlign: "right" }}>V. Unit. (R$)</th>
                  <th style={{ ...thBulk, width: 110, textAlign: "right" }}>Total</th>
                  <th style={{ ...thBulk, width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((l, idx) => {
                  const total = (Number(l.quantidade) || 0) * (Number(l.valor_unitario) || 0);
                  return (
                    <tr key={l.id || `nova-${idx}`} style={{ borderTop: `1px solid ${line}` }} data-testid={`bulk-linha-${idx}`}>
                      <td style={tdBulk}>
                        {idx + 1}
                        {grupoEdit && (
                          <div style={{ fontSize: 8, letterSpacing: 1, textTransform: "uppercase", color: l.id ? ink3 : gold }} data-testid={`bulk-linha-status-${idx}`}>
                            {l.id ? "salva" : "nova"}
                          </div>
                        )}
                      </td>
                      <td style={tdBulk}>
                        <select value={l.tipo} onChange={e => updateLinha(idx, { tipo: e.target.value })} style={inpBulk} data-testid={`bulk-tipo-${idx}`}>
                          <option value="colocacao">Colocação</option>
                          <option value="frete">Frete</option>
                          <option value="medicao">Medição</option>
                        </select>
                      </td>
                      <td style={tdBulk}>
                        <select value={l.prestador_ref} onChange={e => updateLinha(idx, { prestador_ref: e.target.value })} style={inpBulk} data-testid={`bulk-prestador-${idx}`}>
                          <option value="">— Selecione —</option>
                          <optgroup label="Prestadores">
                            {opcoesPrestador.filter(o => o.group === "Prestadores").map(o => (
                              <option key={o.ref} value={o.ref}>{o.label}</option>
                            ))}
                          </optgroup>
                          <optgroup label="Terceirizados">
                            {opcoesPrestador.filter(o => o.group === "Terceirizados").map(o => (
                              <option key={o.ref} value={o.ref}>{o.label}</option>
                            ))}
                          </optgroup>
                        </select>
                      </td>
                      <td style={tdBulk}>
                        <input
                          list="cps-servicos-catalogo"
                          value={l.descricao}
                          onChange={e => {
                            const val = e.target.value;
                            const uniAuto = UNIDADE_POR_DESCRICAO[val];
                            updateLinha(idx, { descricao: val, ...(uniAuto ? { unidade: uniAuto } : {}) });
                          }}
                          style={inpBulk}
                          placeholder="Ex: Colocação de bancada"
                          data-testid={`bulk-descricao-${idx}`}
                        />
                      </td>
                      <td style={tdBulk}>
                        <input value={l.unidade} onChange={e => updateLinha(idx, { unidade: e.target.value })} style={inpBulk} placeholder="m²" data-testid={`bulk-unidade-${idx}`} />
                      </td>
                      <td style={tdBulk}>
                        <input type="number" step="0.01" min="0.01" value={l.quantidade} onChange={e => updateLinha(idx, { quantidade: e.target.value })} style={{ ...inpBulk, textAlign: "right" }} data-testid={`bulk-qtd-${idx}`} />
                      </td>
                      <td style={tdBulk}>
                        <input type="number" step="0.01" min="0" value={l.valor_unitario} onChange={e => updateLinha(idx, { valor_unitario: e.target.value })} style={{ ...inpBulk, textAlign: "right" }} data-testid={`bulk-vu-${idx}`} />
                      </td>
                      <td style={{ ...tdBulk, textAlign: "right", color: gold, fontWeight: 600 }}>{brl(total)}</td>
                      <td style={{ ...tdBulk, textAlign: "center" }}>
                        <button onClick={() => removeLinha(idx)} style={{ background: "transparent", border: "none", color: "#c47a7a", cursor: "pointer", fontSize: 16, padding: 4 }} title="Remover linha" data-testid={`bulk-del-${idx}`}>×</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: `2px solid ${line}` }}>
                  <td colSpan={7} style={{ ...tdBulk, textAlign: "right", color: ink3, fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase" }}>Total geral</td>
                  <td style={{ ...tdBulk, textAlign: "right", color: gold, fontFamily: "'Fraunces',serif", fontSize: 18, fontWeight: 500 }} data-testid="bulk-total-geral">{brl(totalBulk)}</td>
                  <td style={tdBulk}></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <button onClick={addLinha} style={btnGhost} data-testid="bulk-add-linha">+ Linha</button>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => { setShowForm(false); resetForm(); }} style={btnGhost} data-testid="bulk-cancel">Cancelar</button>
              <button onClick={salvarBulk} disabled={salvandoBulk} style={btnPrimary} data-testid="bulk-save">
                {salvandoBulk ? "Salvando…" : grupoEdit ? "Salvar alterações" : `Registrar ${linhas.filter(l => l.descricao || l.valor_unitario).length || 1} serviço(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Form de adiantamento */}
      {showAdiantForm && (
        <div style={{ background: bg2, border: `1px solid ${line}`, borderRadius: 6, padding: 18, marginBottom: 18 }} data-testid="adiant-form">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={lbl}>Prestador / Terceirizado</label>
              <select value={adiantForm.prestador_ref} onChange={e => setAdiantForm({ ...adiantForm, prestador_ref: e.target.value })} style={inp} data-testid="adiant-form-prestador">
                <option value="">— Selecione —</option>
                <optgroup label="Prestadores">
                  {opcoesPrestador.filter(o => o.group === "Prestadores").map(o => (
                    <option key={o.ref} value={o.ref}>{o.label}</option>
                  ))}
                </optgroup>
                <optgroup label="Terceirizados">
                  {opcoesPrestador.filter(o => o.group === "Terceirizados").map(o => (
                    <option key={o.ref} value={o.ref}>{o.label}</option>
                  ))}
                </optgroup>
              </select>
            </div>
            <div>
              <label style={lbl}>Valor (R$)</label>
              <input type="number" step="0.01" min="0.01" value={adiantForm.valor} onChange={e => setAdiantForm({ ...adiantForm, valor: e.target.value })} style={inp} data-testid="adiant-form-valor" />
            </div>
            <div>
              <label style={lbl}>Data</label>
              <input type="date" value={adiantForm.data} onChange={e => setAdiantForm({ ...adiantForm, data: e.target.value })} style={inp} data-testid="adiant-form-data" />
            </div>
            <div>
              <label style={lbl}>Descrição (opcional)</label>
              <input value={adiantForm.descricao} onChange={e => setAdiantForm({ ...adiantForm, descricao: e.target.value })} style={inp} placeholder="Ex: PIX 10/02" data-testid="adiant-form-descricao" />
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
            <button onClick={() => setShowAdiantForm(false)} style={btnGhost}>Cancelar</button>
            <button onClick={salvarAdiant} style={btnPrimary} data-testid="adiant-form-save">Registrar Adiantamento</button>
          </div>
        </div>
      )}

      {/* Grupos por prestador */}
      {gruposPorPrestador.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: ink3, border: `1px dashed ${line}`, borderRadius: 6 }}>
          Nenhum serviço registrado. Clique em <strong style={{ color: gold }}>+ Registrar Serviço</strong> para começar.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {gruposPorPrestador.map(g => (
            <React.Fragment key={g.key}>
            <div style={{ background: bg2, border: `1px solid ${line}`, borderRadius: 6, padding: 18 }} data-testid={`grupo-${g.key}`}>
              {g.primeiro && <SinistrosPendentesAlert nome={g.nome} obraId={obraId} headers={headers} />}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 14, paddingBottom: 12, borderBottom: `1px solid ${line}` }}>
                <div>
                  <div style={{ fontFamily: "'Fraunces',serif", fontSize: 18, color: ink, fontWeight: 400 }} data-testid={`grupo-titulo-${g.key}`}>
                    {g.nome}{g.data ? ` — ${fmtDataBR(g.data)}` : " — sem data"}
                  </div>
                  <div style={{ fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", color: ink3, marginTop: 3 }}>
                    {g.kind === "prestador" ? "Prestador" : "Terceirizado"} · {g.servicos.length} serviço(s)
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", color: ink3 }}>Saldo a pagar</div>
                    <div style={{ fontFamily: "'Fraunces',serif", fontSize: 22, color: gold, fontWeight: 500 }}>
                      {brl(g.total - g.total_adiant - g.total_retrab)}
                    </div>
                  </div>
                  <button
                    onClick={() => startGrupoEdit(g)}
                    style={{ ...btnGhost, color: gold }}
                    data-testid={`btn-add-linhas-${g.key}`}
                    title="Reabrir as linhas deste prestador para editar e acrescentar itens"
                  >
                    + Adicionar linhas
                  </button>
                  <button
                    onClick={() => window.open(`/recibo/${obraId}/${g.kind}/${g.id}?data=${encodeURIComponent(g.data)}`, "_blank")}
                    style={btnGhost}
                    data-testid={`btn-recibo-${g.key}`}
                    title="Abrir recibo local pra impressão"
                  >
                    Ver / Imprimir
                  </button>
                  <button
                    onClick={async () => {
                      setGerandoLink(true);
                      try {
                        const { data } = await axios.post(
                          `${API}/obras/${obraId}/servicos/recibos`,
                          { prestador_kind: g.kind, prestador_id: g.id, data: g.data, forma_pagamento: "PIX" },
                          { headers },
                        );
                        setLinkModal({
                          token: data.public_token,
                          prestador_nome: g.nome,
                          telefone: (g.kind === "prestador"
                            ? prestadores
                            : terceirizados
                          ).find(x => x.id === g.id)?.telefone || "",
                          saldo: data.snapshot?.saldo,
                        });
                      } catch (e) {
                        alert("Erro: " + (e?.response?.data?.detail || e.message));
                      } finally {
                        setGerandoLink(false);
                      }
                    }}
                    disabled={gerandoLink}
                    style={btnPrimary}
                    data-testid={`btn-gerar-link-${g.key}`}
                  >
                    {gerandoLink ? "Gerando…" : "Gerar Link p/ Prestador"}
                  </button>
                </div>
              </div>

              {/* tabela de serviços */}
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: ink3, fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase" }}>
                      <th style={th}>Data</th>
                      <th style={th}>Tipo</th>
                      <th style={th}>Descrição</th>
                      <th style={{ ...th, textAlign: "right" }}>Qtd</th>
                      <th style={th}>Un.</th>
                      <th style={{ ...th, textAlign: "right" }}>V. Unit.</th>
                      <th style={{ ...th, textAlign: "right" }}>Total</th>
                      <th style={{ ...th, width: 90 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.servicos.map(s => (
                      <tr key={s.id} style={{ borderTop: `1px solid ${line}` }}>
                        <td style={td}>{fmtDataBR(s.data)}</td>
                        <td style={td}><span style={tipoBadgeStyle(s.tipo)}>{TIPO_LABEL[s.tipo] || s.tipo}</span></td>
                        <td style={td}>{s.descricao}{s.observacoes && <div style={{ color: ink3, fontSize: 11, marginTop: 2 }}>{s.observacoes}</div>}</td>
                        <td style={{ ...td, textAlign: "right" }}>{s.quantidade}</td>
                        <td style={{ ...td, color: ink3 }}>{s.unidade || "—"}</td>
                        <td style={{ ...td, textAlign: "right" }}>{brl(s.valor_unitario)}</td>
                        <td style={{ ...td, textAlign: "right", color: ink, fontWeight: 500 }}>{brl(s.valor_total)}</td>
                        <td style={{ ...td, textAlign: "right" }}>
                          <button onClick={() => startEdit(s)} style={{ ...btnGhost, padding: "4px 10px", fontSize: 11, marginRight: 6 }} data-testid={`btn-edit-${s.id}`}>Editar</button>
                          <button onClick={() => remover(s.id)} style={{ ...btnDanger, padding: "4px 10px", fontSize: 11 }} data-testid={`btn-del-${s.id}`}>×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* adiantamentos */}
              {g.adiantamentos.length > 0 && (
                <div style={{ marginTop: 14, background: bg1, border: `1px solid ${line}`, borderRadius: 4, padding: 12 }}>
                  <div style={{ fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", color: ink3, marginBottom: 8 }}>
                    Adiantamentos ({brl(g.total_adiant)})
                  </div>
                  {g.adiantamentos.map(a => (
                    <div key={a.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderTop: `1px solid ${line}`, fontSize: 13 }}>
                      <div>
                        <span style={{ color: ink3 }}>{fmtDataBR(a.data)}</span>
                        {" — "}
                        <span>{a.descricao || "Adiantamento"}</span>
                      </div>
                      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <span style={{ color: ink, fontWeight: 500 }}>{brl(a.valor)}</span>
                        <button onClick={() => removerAdiant(a.id)} style={{ ...btnDanger, padding: "2px 8px", fontSize: 11 }} data-testid={`btn-del-adiant-${a.id}`}>×</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <RetrabalhoBox g={g} podeEditar={podeRetrab} obraId={obraId} headers={headers} onReload={load} />
              <SaldoBloco g={g} />
            </div>
            {g.resumo && (
              <div
                style={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: "6px 18px", padding: "10px 18px", marginTop: -6, border: `1px dashed ${line}`, borderRadius: 6, fontSize: 12, color: ink3 }}
                data-testid={`resumo-prestador-${g.ref}`}
              >
                <span style={{ marginRight: "auto", fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase" }}>Resumo Total · {g.nome}</span>
                <span>Total de Serviços: <strong style={{ color: ink }}>{brl(g.resumo.total)}</strong></span>
                <span>|</span>
                <span>Total de Adiantamentos: <strong style={{ color: ink }}>{brl(g.resumo.total_adiant)}</strong></span>
                <span>|</span>
                <span>Total de Retrabalho: <strong style={{ color: "#c47a7a" }} data-testid={`resumo-retrab-${g.ref}`}>− {brl(g.resumo.total_retrab)}</strong></span>
                <span>|</span>
                <span>Saldo Geral: <strong style={{ color: gold }} data-testid={`resumo-saldo-${g.ref}`}>{brl(g.resumo.total - g.resumo.total_adiant - g.resumo.total_retrab)}</strong></span>
              </div>
            )}
            </React.Fragment>
          ))}
        </div>
      )}

      {linkModal && <LinkGeradoModal data={linkModal} onClose={() => setLinkModal(null)} />}

      {/* Histórico de Recibos */}
      <HistoricoRecibos
        recibos={recibos}
        filtroPrest={histFiltroPrest} setFiltroPrest={setHistFiltroPrest}
        filtroStatus={histFiltroStatus} setFiltroStatus={setHistFiltroStatus}
        prestadores={prestadores} terceirizados={terceirizados}
        obraId={obraId} headers={headers}
        onReload={load}
      />
    </div>
  );
}

function HistoricoRecibos({ recibos, filtroPrest, setFiltroPrest, filtroStatus, setFiltroStatus, prestadores, terceirizados, obraId, headers, onReload }) {
  const [expandido, setExpandido] = useState(false);

  // Enriquece com nome do prestador
  const enriquecidos = useMemo(() => {
    return (recibos || []).map(r => {
      const kind = r.prestador_kind;
      const arr = kind === "prestador" ? prestadores : terceirizados;
      const nome = (arr.find(x => x.id === r.prestador_id) || {}).nome
        || (r.snapshot?.prestador?.nome) || "(removido)";
      return { ...r, prestador_nome: nome, prestador_ref: `${kind}:${r.prestador_id}` };
    });
  }, [recibos, prestadores, terceirizados]);

  const filtrados = useMemo(() => {
    return enriquecidos.filter(r => {
      if (filtroStatus !== "todos" && r.status !== filtroStatus) return false;
      if (filtroPrest !== "todos" && r.prestador_ref !== filtroPrest) return false;
      return true;
    });
  }, [enriquecidos, filtroPrest, filtroStatus]);

  const prestUnicos = useMemo(() => {
    const seen = new Map();
    enriquecidos.forEach(r => { if (!seen.has(r.prestador_ref)) seen.set(r.prestador_ref, r.prestador_nome); });
    return Array.from(seen, ([ref, nome]) => ({ ref, nome }));
  }, [enriquecidos]);

  const cancelar = async (rid) => {
    if (!window.confirm("Cancelar/remover este recibo? Se estiver assinado, será marcado como cancelado (histórico preservado). Se pendente, será removido.")) return;
    try {
      await axios.delete(`${API}/obras/${obraId}/servicos/recibos/${rid}`, { headers });
      await onReload();
    } catch (e) {
      alert("Erro: " + (e?.response?.data?.detail || e.message));
    }
  };

  const copiarLink = async (token) => {
    const url = `${window.location.origin}/r/${token}`;
    try { await navigator.clipboard.writeText(url); alert("Link copiado: " + url); }
    catch { alert(url); }
  };

  if (!recibos || recibos.length === 0) return null;

  const statusBadge = (s) => {
    const map = { pendente: gold, assinado: "#5fa86c", cancelado: "#c47a7a" };
    return {
      fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase",
      color: map[s] || ink3, border: `1px solid ${map[s] || ink3}`,
      padding: "2px 8px", borderRadius: 2,
    };
  };

  return (
    <div style={{ marginTop: 28, background: bg2, border: `1px solid ${line}`, borderRadius: 6 }} data-testid="historico-recibos">
      <button
        onClick={() => setExpandido(v => !v)}
        style={{
          width: "100%", background: "transparent", border: "none", color: ink,
          padding: "16px 18px", display: "flex", justifyContent: "space-between",
          alignItems: "center", cursor: "pointer", textAlign: "left",
        }}
        data-testid="historico-toggle"
      >
        <div>
          <div style={{ fontFamily: "'Fraunces',serif", fontSize: 18, fontWeight: 400 }}>Histórico de Recibos</div>
          <div style={{ fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", color: ink3, marginTop: 3 }}>
            {recibos.length} recibo(s) · {recibos.filter(r => r.status === "assinado").length} assinado(s) · {recibos.filter(r => r.status === "pendente").length} pendente(s)
          </div>
        </div>
        <span style={{ color: ink3, fontSize: 20 }}>{expandido ? "−" : "+"}</span>
      </button>

      {expandido && (
        <div style={{ padding: "0 18px 18px" }}>
          <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 200px" }}>
              <label style={lbl}>Prestador</label>
              <select value={filtroPrest} onChange={e => setFiltroPrest(e.target.value)} style={inp} data-testid="hist-filtro-prestador">
                <option value="todos">Todos os prestadores</option>
                {prestUnicos.map(p => <option key={p.ref} value={p.ref}>{p.nome}</option>)}
              </select>
            </div>
            <div style={{ flex: "0 1 200px" }}>
              <label style={lbl}>Status</label>
              <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)} style={inp} data-testid="hist-filtro-status">
                <option value="todos">Todos os status</option>
                <option value="pendente">Pendente</option>
                <option value="assinado">Assinado</option>
                <option value="cancelado">Cancelado</option>
              </select>
            </div>
          </div>

          {filtrados.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", color: ink3, fontSize: 12 }}>Nenhum recibo com esses filtros.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: "left", color: ink3, fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase" }}>
                    <th style={th}></th>
                    <th style={th}>Criado</th>
                    <th style={th}>Prestador</th>
                    <th style={{ ...th, textAlign: "right" }}>Saldo</th>
                    <th style={{ ...th, textAlign: "right" }}>Pago</th>
                    <th style={th}>Status</th>
                    <th style={th}>Assinado</th>
                    <th style={{ ...th, textAlign: "right" }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filtrados.map(r => (
                    <ReciboRow
                      key={r.id}
                      recibo={r}
                      obraId={obraId}
                      headers={headers}
                      onReload={onReload}
                      onCancel={cancelar}
                      onCopiarLink={copiarLink}
                      statusBadge={statusBadge}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ReciboRow({ recibo: r, obraId, headers, onReload, onCancel, onCopiarLink, statusBadge }) {
  const [aberto, setAberto] = useState(false);
  const [novoValor, setNovoValor] = useState("");
  const [novaData, setNovaData] = useState(new Date().toISOString().slice(0, 10));
  const [novaForma, setNovaForma] = useState("PIX");
  const [novaObs, setNovaObs] = useState("");
  const [jaRecebido, setJaRecebido] = useState(true); // default: pago (checkbox marcado)
  const [salvando, setSalvando] = useState(false);
  const pagamentos = r.pagamentos || [];
  const totalPago = r.total_pago || 0;
  const totalPendente = r.total_pendente || 0;
  const saldoRest = r.saldo_restante ?? (r.snapshot?.saldo || 0);
  const quitado = r.quitado === true;
  const sobra = saldoRest < 0;
  const podeAdicionar = r.status !== "cancelado";

  const registrar = async () => {
    const valor = parseFloat(String(novoValor).replace(",", "."));
    if (!valor || valor <= 0) { toastError("Informe um valor maior que zero"); return; }
    if (!novaData) { toastError("Informe a data do pagamento"); return; }
    setSalvando(true);
    try {
      await axios.post(
        `${API}/obras/${obraId}/servicos/recibos/${r.id}/pagamentos`,
        {
          data: novaData,
          valor,
          forma_pagamento: novaForma || null,
          observacoes: novaObs.trim() || null,
          status: jaRecebido ? "pago" : "pendente",
        },
        { headers },
      );
      setNovoValor("");
      setNovaObs("");
      const eraRecebido = jaRecebido;
      setJaRecebido(true);
      await onReload();
      toastSuccess(
        eraRecebido ? "Pagamento registrado como PAGO" : "Pagamento registrado como PENDENTE",
        `${brl(valor)} em ${novaData.split("-").reverse().join("/")}.`,
      );
    } catch (e) {
      toastError("Não conseguimos salvar o pagamento", { description: pickErrorMsg(e), onRetry: registrar });
    } finally {
      setSalvando(false);
    }
  };

  const togglePagamentoStatus = async (p) => {
    const novo = (p.status || "pago") === "pago" ? "pendente" : "pago";
    try {
      await axios.patch(
        `${API}/obras/${obraId}/servicos/recibos/${r.id}/pagamentos/${p.id}`,
        { status: novo },
        { headers },
      );
      await onReload();
      toastSuccess(novo === "pago" ? "Marcado como PAGO" : "Marcado como PENDENTE");
    } catch (e) {
      toastError("Erro ao alternar status", { description: pickErrorMsg(e) });
    }
  };

  const [editPagId, setEditPagId] = useState(null);
  const salvarPagamento = async (pid, body) => {
    try {
      await axios.patch(`${API}/obras/${obraId}/servicos/recibos/${r.id}/pagamentos/${pid}`, body, { headers });
      setEditPagId(null);
      await onReload();
      toastSuccess("Pagamento corrigido");
    } catch (e) {
      toastError("Não conseguimos salvar a correção", { description: pickErrorMsg(e) });
    }
  };

  const removerPagamento = async (pid) => {
    if (!window.confirm("Remover este pagamento? A ação fica no histórico de auditoria.")) return;
    try {
      await axios.delete(
        `${API}/obras/${obraId}/servicos/recibos/${r.id}/pagamentos/${pid}`,
        { headers },
      );
      await onReload();
      toastSuccess("Pagamento removido");
    } catch (e) {
      toastError("Não conseguimos remover", { description: pickErrorMsg(e) });
    }
  };

  const pagtoStatusBadge = (s) => {
    const isPago = (s || "pago") === "pago";
    return {
      fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase",
      color: isPago ? "#5fa86c" : "#c9a144",
      border: `1px solid ${isPago ? "#5fa86c" : "#c9a144"}`,
      padding: "2px 6px", borderRadius: 2,
      background: "transparent", cursor: "pointer",
      display: "inline-block",
    };
  };

  return (
    <>
      <tr style={{ borderTop: `1px solid ${line}` }} data-testid={`recibo-row-${r.id}`}>
        <td style={{ ...td, width: 32 }}>
          <button
            onClick={() => setAberto(v => !v)}
            style={{ background: "transparent", border: `1px solid ${line}`, color: ink2,
                     width: 22, height: 22, cursor: "pointer", fontSize: 11 }}
            data-testid={`btn-expandir-${r.id}`}
            title={aberto ? "Recolher pagamentos" : "Ver/adicionar pagamentos"}
          >
            {aberto ? "−" : "+"}
          </button>
        </td>
        <td style={td}>{new Date(r.created_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}</td>
        <td style={td}>{r.prestador_nome}</td>
        <td style={{ ...td, textAlign: "right", color: ink }}>{brl(r.snapshot?.saldo || 0)}</td>
        <td style={{ ...td, textAlign: "right" }}>
          <div style={{ color: totalPago > 0 ? "#5fa86c" : ink3 }} data-testid={`recibo-total-pago-${r.id}`}>
            {brl(totalPago)}
          </div>
          {totalPendente > 0 && (
            <div style={{ fontSize: 10, color: "#c9a144", marginTop: 2 }}
                 data-testid={`recibo-total-pendente-${r.id}`}>
              + {brl(totalPendente)} pend.
            </div>
          )}
          {pagamentos.length > 0 && (
            <div style={{ fontSize: 10, color: ink3, marginTop: 2 }}>
              {pagamentos.length} pagto(s)
            </div>
          )}
        </td>
        <td style={td}>
          <span style={statusBadge(r.status)}>{r.status}</span>
          {quitado && (
            <span
              style={{
                fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase",
                color: "#5fa86c", border: "1px solid #5fa86c",
                padding: "2px 6px", borderRadius: 2, marginLeft: 6,
              }}
              data-testid={`badge-quitado-${r.id}`}
            >
              QUITADO
            </span>
          )}
          {sobra && (
            <span
              style={{
                fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase",
                color: "#c9a144", border: "1px solid #c9a144",
                padding: "2px 6px", borderRadius: 2, marginLeft: 6,
              }}
              data-testid={`badge-sobra-${r.id}`}
              title={`Pagamentos ultrapassam o saldo em ${brl(-saldoRest)}`}
            >
              SOBRA {brl(-saldoRest)}
            </span>
          )}
        </td>
        <td style={{ ...td, fontSize: 12 }}>
          {r.assinado_em ? (
            <>
              <div>{r.assinado_nome || "—"}</div>
              <div style={{ color: ink3, fontSize: 11 }}>{new Date(r.assinado_em).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}</div>
            </>
          ) : "—"}
        </td>
        <td style={{ ...td, textAlign: "right" }}>
          <button onClick={() => window.open(`/r/${r.public_token}`, "_blank")} style={{ ...btnGhost, padding: "4px 10px", fontSize: 11, marginRight: 6 }} data-testid={`btn-open-${r.id}`}>Abrir</button>
          <button onClick={() => onCopiarLink(r.public_token)} style={{ ...btnGhost, padding: "4px 10px", fontSize: 11, marginRight: 6 }} data-testid={`btn-copy-${r.id}`}>Link</button>
          <button onClick={() => onCancel(r.id)} style={{ ...btnDanger, padding: "4px 10px", fontSize: 11 }} data-testid={`btn-cancel-${r.id}`}>×</button>
        </td>
      </tr>
      {aberto && (
        <tr style={{ background: bg1 }} data-testid={`recibo-pagamentos-${r.id}`}>
          <td colSpan={8} style={{ padding: "14px 18px 20px" }}>
            <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: ink3, marginBottom: 10 }}>
              Pagamentos parciais
            </div>

            {pagamentos.length === 0 ? (
              <div style={{ fontSize: 12, color: ink3, marginBottom: 14, fontStyle: "italic" }}>
                Nenhum pagamento registrado. Saldo pendente: <strong style={{ color: ink }}>{brl(r.snapshot?.saldo || 0)}</strong>.
              </div>
            ) : (
              <table style={{ width: "100%", fontSize: 12, marginBottom: 14, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ color: ink3, fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase" }}>
                    <th style={{ ...th, padding: "6px 8px" }}>Data</th>
                    <th style={{ ...th, padding: "6px 8px", textAlign: "right" }}>Valor</th>
                    <th style={{ ...th, padding: "6px 8px" }}>Forma</th>
                    <th style={{ ...th, padding: "6px 8px" }}>Obs.</th>
                    <th style={{ ...th, padding: "6px 8px" }}>Status</th>
                    <th style={{ ...th, padding: "6px 8px", textAlign: "right" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {pagamentos.map(p => {
                    const isPago = (p.status || "pago") === "pago";
                    if (editPagId === p.id) {
                      return <PagamentoEditRow key={p.id} p={p} onSave={body => salvarPagamento(p.id, body)} onCancel={() => setEditPagId(null)} />;
                    }
                    return (
                    <tr key={p.id} style={{ borderTop: `1px solid ${line}` }} data-testid={`pagamento-${p.id}`}>
                      <td style={{ padding: "6px 8px" }}>{(p.data || "").split("-").reverse().join("/")}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right",
                                   color: isPago ? "#5fa86c" : "#c9a144" }}>{brl(p.valor)}</td>
                      <td style={{ padding: "6px 8px", color: ink2 }}>{p.forma_pagamento || "—"}</td>
                      <td style={{ padding: "6px 8px", color: ink3 }}>{p.observacoes || ""}</td>
                      <td style={{ padding: "6px 8px" }}>
                        <button
                          onClick={() => togglePagamentoStatus(p)}
                          style={pagtoStatusBadge(p.status)}
                          title="Clique para alternar entre pago e pendente"
                          data-testid={`btn-toggle-status-${p.id}`}
                        >
                          {isPago ? "PAGO" : "PENDENTE"}
                        </button>
                      </td>
                      <td style={{ padding: "6px 8px", textAlign: "right", whiteSpace: "nowrap" }}>
                        <BtnEditPagamento onClick={() => setEditPagId(p.id)} testid={`btn-editar-pagamento-${p.id}`} />
                        <button
                          onClick={() => removerPagamento(p.id)}
                          style={{ ...btnDanger, padding: "2px 8px", fontSize: 10 }}
                          data-testid={`btn-remover-pagamento-${p.id}`}
                          title="Remover pagamento"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  );})}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: `2px solid ${line}` }}>
                    <td style={{ padding: "8px", fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", color: ink3 }}>
                      Total pago
                    </td>
                    <td style={{ padding: "8px", textAlign: "right", color: "#5fa86c", fontWeight: 600 }}>
                      {brl(totalPago)}
                    </td>
                    <td colSpan={2} style={{ padding: "8px", fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", color: ink3 }}>
                      {totalPendente > 0 ? `Pendente: ${brl(totalPendente)}` : ""}
                    </td>
                    <td style={{ padding: "8px", fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", color: ink3 }}>
                      Saldo restante
                    </td>
                    <td style={{
                      padding: "8px", textAlign: "right", fontWeight: 600,
                      color: quitado ? "#5fa86c" : (sobra ? "#c9a144" : gold),
                    }}
                      data-testid={`recibo-saldo-restante-${r.id}`}
                    >
                      {brl(saldoRest)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}

            {podeAdicionar ? (
              <div style={{
                background: bg2, border: `1px solid ${line}`, padding: 12,
                display: "grid", gridTemplateColumns: "120px 120px 120px 1fr auto auto", gap: 10, alignItems: "end",
              }}>
                <div>
                  <label style={lbl}>Data</label>
                  <input type="date" value={novaData} onChange={e => setNovaData(e.target.value)}
                         style={inp} data-testid={`input-nova-data-${r.id}`} />
                </div>
                <div>
                  <label style={lbl}>Valor</label>
                  <input type="number" step="0.01" min="0.01" value={novoValor}
                         onChange={e => setNovoValor(e.target.value)}
                         placeholder="0,00" style={inp} data-testid={`input-novo-valor-${r.id}`} />
                </div>
                <div>
                  <label style={lbl}>Forma</label>
                  <select value={novaForma} onChange={e => setNovaForma(e.target.value)} style={inp}
                          data-testid={`input-nova-forma-${r.id}`}>
                    <option value="PIX">PIX</option>
                    <option value="Dinheiro">Dinheiro</option>
                    <option value="Transferência">Transferência</option>
                    <option value="Cheque">Cheque</option>
                    <option value="Cartão">Cartão</option>
                    <option value="Boleto">Boleto</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>
                <div>
                  <label style={lbl}>Observações (opcional)</label>
                  <input value={novaObs} onChange={e => setNovaObs(e.target.value)}
                         placeholder="Ex: 1ª parcela" style={inp}
                         data-testid={`input-nova-obs-${r.id}`} />
                </div>
                <label style={{
                  display: "flex", alignItems: "center", gap: 6,
                  fontSize: 11, color: ink2, cursor: "pointer",
                  padding: "10px 8px", whiteSpace: "nowrap",
                }} title="Marcado: valor já foi recebido (pago). Desmarcado: promessa/previsão (pendente).">
                  <input
                    type="checkbox"
                    checked={jaRecebido}
                    onChange={e => setJaRecebido(e.target.checked)}
                    data-testid={`input-ja-recebido-${r.id}`}
                    style={{ cursor: "pointer" }}
                  />
                  Já recebido
                </label>
                <button
                  onClick={registrar}
                  disabled={salvando}
                  style={{ ...btnPrimary, padding: "10px 16px", fontSize: 11 }}
                  data-testid={`btn-registrar-pagamento-${r.id}`}
                >
                  {salvando ? "…" : "Registrar"}
                </button>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: ink3, fontStyle: "italic" }}>
                Recibo cancelado — não é possível registrar novos pagamentos.
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function LinkGeradoModal({ data, onClose }) {
  const [copiado, setCopiado] = useState(false);
  const publicUrl = `${window.location.origin}/r/${data.token}`;
  const msgWhats = encodeURIComponent(
    `Olá ${data.prestador_nome}, segue o recibo de pagamento CAMASA no valor de ${brl(data.saldo)}. Confira os itens e assine digitalmente:\n\n${publicUrl}`,
  );
  const telClean = (data.telefone || "").replace(/\D/g, "");
  const whatsHref = telClean
    ? `https://wa.me/${telClean.startsWith("55") ? telClean : "55" + telClean}?text=${msgWhats}`
    : `https://wa.me/?text=${msgWhats}`;

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      alert("Copie manualmente: " + publicUrl);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}
      data-testid="link-modal"
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: bg2, border: `1px solid ${line}`, padding: 28,
          maxWidth: 560, width: "100%", boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
        }}
      >
        <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 24, color: ink, margin: "0 0 8px", fontWeight: 400 }}>
          Link gerado
        </h3>
        <p style={{ color: ink3, fontSize: 13, margin: "0 0 20px", lineHeight: 1.6 }}>
          O prestador <strong style={{ color: ink }}>{data.prestador_nome}</strong> pode conferir e assinar digitalmente o recibo de <strong style={{ color: gold }}>{brl(data.saldo)}</strong> pelo link abaixo. Depois de assinado, o valor trava.
        </p>

        <label style={lbl}>URL do recibo</label>
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          <input value={publicUrl} readOnly style={{ ...inp, flex: 1 }} onFocus={e => e.target.select()} data-testid="link-modal-url" />
          <button onClick={copiar} style={btnGhost} data-testid="link-modal-copy">{copiado ? "✓ Copiado" : "Copiar"}</button>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
          <a href={whatsHref} target="_blank" rel="noreferrer" style={{ ...btnPrimary, background: "#25d366", textDecoration: "none", flex: 1, textAlign: "center" }} data-testid="link-modal-whatsapp">
            Enviar por WhatsApp
          </a>
          <button onClick={onClose} style={btnGhost} data-testid="link-modal-close">Fechar</button>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, valor, tone, testid }) {
  const color = tone === "gold" ? gold : tone === "muted" ? ink3 : ink;
  return (
    <div style={{ background: bg2, border: `1px solid ${line}`, padding: 14, borderRadius: 4 }} data-testid={testid}>
      <div style={{ fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", color: ink3 }}>{label}</div>
      <div style={{ fontFamily: "'Fraunces',serif", fontSize: 26, color, fontWeight: 500, marginTop: 4 }}>{valor}</div>
    </div>
  );
}

const th = { padding: "8px 10px", fontWeight: 500 };
const td = { padding: "10px", color: ink2, verticalAlign: "top" };

// Estilos compactos para o form multi-linha (tipo NF)
const thBulk = { padding: "6px 8px", fontWeight: 500, whiteSpace: "nowrap" };
const tdBulk = { padding: "6px 4px", verticalAlign: "middle" };
const inpBulk = {
  width: "100%", background: bg1, border: `1px solid ${line}`,
  color: ink, padding: "6px 8px", fontSize: 12, borderRadius: 3, boxSizing: "border-box",
};

/* ============================================================
 * Recibo de Serviços — página de impressão (window.print)
 * Rota: /recibo/:obra_id/:kind/:pid
 * Cabeçalho CPS, tabela de itens, adiantamentos, saldo, texto legal, assinatura
 * ============================================================ */
export function ReciboPrintView() {
  const { obraId, kind, pid } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [erro, setErro] = useState(null);
  const [pagamento, setPagamento] = useState("PIX");

  const token = localStorage.getItem("cps_token");

  useEffect(() => {
    const dataVisita = new URLSearchParams(window.location.search).get("data");
    const qs = dataVisita !== null ? `?data=${encodeURIComponent(dataVisita)}` : "";
    axios.get(`${API}/obras/${obraId}/servicos/recibo/${kind}/${pid}${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(e => setErro(e?.response?.data?.detail || e.message));
  }, [obraId, kind, pid, token]);

  const printar = () => window.print();

  if (erro) return <div style={{ padding: 40, color: "#c47a7a" }}>Erro ao carregar recibo: {erro}</div>;
  if (!data) return <div style={{ padding: 40, color: ink3 }}>Carregando recibo…</div>;

  const tipoDoc = decideTipoRecibo(data.tipos);

  return (
    <>
      <style>{`
        @page { size: A4; margin: 15mm 12mm; }
        body { background: #ffffff; }
        .rec-noprint { display: block; }
        @media print {
          html, body, #root, .app { background: #ffffff !important; }
          .rec-noprint { display: none !important; }
          .rec-doc { box-shadow: none !important; margin: 0 !important; }
          /* Neutraliza o footer global do App.css @page */
          @page { margin: 15mm 12mm; }
        }
        ${RECIBO_MOBILE_CSS}
      `}</style>

      <div className="rec-noprint" style={{ background: "#1a1820", padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, zIndex: 10 }}>
        <button onClick={() => nav(-1)} style={btnGhost}>← Voltar</button>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <label style={{ fontSize: 12, color: ink2 }}>
            Forma de pagamento:{" "}
            <select value={pagamento} onChange={e => setPagamento(e.target.value)} style={{ ...inp, display: "inline-block", width: "auto", padding: "4px 8px" }}>
              <option>PIX</option>
              <option>Dinheiro</option>
              <option>Transferência</option>
              <option>Cheque</option>
            </select>
          </label>
          <button onClick={printar} style={btnPrimary} data-testid="btn-print">Imprimir / Salvar PDF</button>
        </div>
      </div>

      <div className="rec-doc" style={{
        maxWidth: 800, margin: "24px auto", background: "#fff", color: "#1a1a1a",
        fontFamily: "Georgia, 'Times New Roman', serif", padding: "48px 56px",
        boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
      }}>
        <ReciboHeader tipoDoc={tipoDoc} data={data} />
        <ReciboPartes data={data} />
        <ReciboTabela servicos={data.servicos} />
        <ReciboAdiantamentos adiantamentos={data.adiantamentos} />
        <ReciboRetrabalhos retrabalhos={data.retrabalhos} />
        <ReciboTotais data={data} pagamento={pagamento} />
        <ReciboTextoLegal tipoDoc={tipoDoc} />
        <ReciboAssinatura data={data} />
      </div>
    </>
  );
}

function decideTipoRecibo(tipos) {
  // Prioridade: se tem colocação → "Prestação de Serviços" (com cláusula ampla);
  // se só frete → "Recibo de Frete"; se só medição → "Prestação de Serviços"
  if (!tipos || tipos.length === 0) return "geral";
  if (tipos.includes("colocacao")) return "colocacao";
  if (tipos.length === 1 && tipos[0] === "frete") return "frete";
  return "geral";
}

// Layout mobile do recibo (prestadores abrem o link no celular).
// Tabela vira cards; grids viram 1 coluna. Não afeta impressão (A4).
const RECIBO_MOBILE_CSS = `
  .rec-cards { display: none; }
  @media screen and (max-width: 640px) {
    .rec-doc { margin: 0 !important; padding: 22px 16px !important; box-shadow: none !important; }
    .rec-table { display: none !important; }
    .rec-cards { display: block; }
    .rec-header-row { flex-direction: column; gap: 10px; }
    .rec-header-row > div:last-child { text-align: left !important; }
    .rec-title { font-size: 22px !important; }
    .rec-partes, .rec-assin-grid, .rec-form-grid { grid-template-columns: 1fr !important; gap: 16px !important; }
    .rec-totais-wrap { justify-content: stretch !important; }
    .rec-totais { min-width: 0 !important; width: 100%; }
    .rec-totais td { padding-left: 4px !important; padding-right: 4px !important; }
    .rec-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
    .rec-scroll table { min-width: 520px; }
    .rec-topbar { flex-wrap: wrap; gap: 10px; padding: 12px 14px !important; }
  }
`;

function ReciboHeader({ tipoDoc, data }) {
  const titulo = tipoDoc === "frete" ? "RECIBO DE FRETE" : "RECIBO DE PRESTAÇÃO DE SERVIÇOS";
  return (
    <div style={{ borderBottom: "2px solid #1a1a1a", paddingBottom: 18, marginBottom: 22 }}>
      <div className="rec-header-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: 3, textTransform: "uppercase", color: "#666" }}>Camasa Process System</div>
          <div className="rec-title" style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 30, fontWeight: 400, marginTop: 4, letterSpacing: -0.5 }}>{titulo}</div>
          {data.consolidado && <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "#8a6a3a", marginTop: 6 }} data-testid="rec-consolidado-tag">Consolidado · todas as visitas na obra</div>}
        </div>
        <div style={{ textAlign: "right", fontSize: 12, color: "#555", lineHeight: 1.6 }}>
          <div><strong>{data.obra?.codigo}</strong></div>
          <div>{data.obra?.nome}</div>
          {data.obra?.cidade && <div>{data.obra.cidade}</div>}
        </div>
      </div>
    </div>
  );
}

function ReciboPartes({ data }) {
  const p = data.prestador || {};
  const o = data.obra || {};
  return (
    <div className="rec-partes" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 22, fontSize: 13, lineHeight: 1.7 }}>
      <div>
        <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "#888", marginBottom: 6 }}>Contratante</div>
        <div><strong>CAMASA MÁRMORES E GRANITOS</strong></div>
        <div style={{ color: "#555" }}>Obra: {o.nome}</div>
        {o.endereco && <div style={{ color: "#555" }}>{o.endereco}</div>}
        {o.cliente_nome && <div style={{ color: "#555" }}>Cliente: {o.cliente_nome}</div>}
      </div>
      <div>
        <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "#888", marginBottom: 6 }}>Prestador de Serviço</div>
        <div><strong>{p.nome || "—"}</strong></div>
        <div style={{ color: "#555" }}>CPF: {p.cpf || <span style={{ borderBottom: "1px dotted #999", display: "inline-block", minWidth: 140 }}>&nbsp;</span>}</div>
        <div style={{ color: "#555" }}>Telefone: {p.telefone || <span style={{ borderBottom: "1px dotted #999", display: "inline-block", minWidth: 140 }}>&nbsp;</span>}</div>
        <div style={{ color: "#555" }}>Localidade: {p.cidade || <span style={{ borderBottom: "1px dotted #999", display: "inline-block", minWidth: 140 }}>&nbsp;</span>}</div>
      </div>
    </div>
  );
}

function ReciboItemCard({ s, idx }) {
  const linha = (label, valor, extra = {}) => (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13, padding: "3px 0", ...extra }}>
      <span style={{ color: "#777" }}>{label}</span>
      <span style={{ textAlign: "right" }}>{valor}</span>
    </div>
  );
  return (
    <div style={{ border: "1px solid #e2ded3", padding: "12px 14px", marginBottom: 10, background: "#fcfbf8" }} data-testid={`rec-card-${idx}`}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", color: "#888", marginBottom: 6 }}>
        <span>{TIPO_LABEL[s.tipo] || s.tipo}</span>
        <span>{fmtDataBR(s.data)}</span>
      </div>
      <div style={{ fontSize: 14, marginBottom: 8, wordBreak: "break-word" }}>
        {s.descricao}
        {s.observacoes && <div style={{ color: "#666", fontSize: 12, marginTop: 2 }}>{s.observacoes}</div>}
      </div>
      {linha("Quantidade", `${s.quantidade}${s.unidade ? " " + s.unidade : ""}`)}
      {linha("Valor unitário", brl(s.valor_unitario))}
      {linha("Total", <strong data-testid={`rec-card-total-${idx}`}>{brl(s.valor_total)}</strong>, { borderTop: "1px solid #e2ded3", marginTop: 4, paddingTop: 6, fontSize: 15 })}
    </div>
  );
}

function ReciboTabela({ servicos }) {
  return (
    <>
    <div className="rec-cards" style={{ marginBottom: 18 }} data-testid="rec-cards">
      {servicos.map((s, i) => <ReciboItemCard key={s.id} s={s} idx={i} />)}
    </div>
    <table className="rec-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 18 }}>
      <thead>
        <tr style={{ borderBottom: "1.5px solid #1a1a1a", textAlign: "left" }}>
          <th style={{ padding: "8px 6px", fontSize: 10, letterSpacing: 2, textTransform: "uppercase" }}>Data</th>
          <th style={{ padding: "8px 6px", fontSize: 10, letterSpacing: 2, textTransform: "uppercase" }}>Tipo</th>
          <th style={{ padding: "8px 6px", fontSize: 10, letterSpacing: 2, textTransform: "uppercase" }}>Descrição</th>
          <th style={{ padding: "8px 6px", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", textAlign: "right" }}>Qtd</th>
          <th style={{ padding: "8px 6px", fontSize: 10, letterSpacing: 2, textTransform: "uppercase" }}>Un.</th>
          <th style={{ padding: "8px 6px", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", textAlign: "right" }}>V. Unit.</th>
          <th style={{ padding: "8px 6px", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", textAlign: "right" }}>Total</th>
        </tr>
      </thead>
      <tbody>
        {servicos.map(s => (
          <tr key={s.id} style={{ borderBottom: "1px solid #eaeaea" }}>
            <td style={{ padding: "8px 6px" }}>{fmtDataBR(s.data)}</td>
            <td style={{ padding: "8px 6px" }}>{TIPO_LABEL[s.tipo] || s.tipo}</td>
            <td style={{ padding: "8px 6px" }}>
              {s.descricao}
              {s.observacoes && <div style={{ color: "#666", fontSize: 11, marginTop: 2 }}>{s.observacoes}</div>}
            </td>
            <td style={{ padding: "8px 6px", textAlign: "right" }}>{s.quantidade}</td>
            <td style={{ padding: "8px 6px", color: "#666" }}>{s.unidade || "—"}</td>
            <td style={{ padding: "8px 6px", textAlign: "right" }}>{brl(s.valor_unitario)}</td>
            <td style={{ padding: "8px 6px", textAlign: "right", fontWeight: 600 }}>{brl(s.valor_total)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </>
  );
}

function ReciboAdiantamentos({ adiantamentos }) {
  if (!adiantamentos || adiantamentos.length === 0) return null;
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "#666", marginBottom: 8 }}>Adiantamentos</div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <tbody>
          {adiantamentos.map(a => (
            <tr key={a.id} style={{ borderBottom: "1px solid #eaeaea" }}>
              <td style={{ padding: "6px", width: 90 }}>{fmtDataBR(a.data)}</td>
              <td style={{ padding: "6px" }}>{a.descricao || "Adiantamento"}</td>
              <td style={{ padding: "6px", textAlign: "right", color: "#a04040" }}>− {brl(a.valor)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReciboTotais({ data, pagamento }) {
  return (
    <div className="rec-totais-wrap" style={{ display: "flex", justifyContent: "flex-end", marginBottom: 22 }}>
      <table className="rec-totais" style={{ fontSize: 13, minWidth: 320 }}>
        <tbody>
          <tr>
            <td style={{ padding: "4px 12px", color: "#666" }}>Subtotal bruto</td>
            <td style={{ padding: "4px 12px", textAlign: "right" }}>{brl(data.subtotal)}</td>
          </tr>
          <tr>
            <td style={{ padding: "4px 12px", color: "#666" }}>Total de adiantamentos</td>
            <td style={{ padding: "4px 12px", textAlign: "right", color: "#a04040" }}>− {brl(data.total_adiantamentos)}</td>
          </tr>
          {data.total_retrabalho > 0 && (
            <tr>
              <td style={{ padding: "4px 12px", color: "#666" }}>Total de retrabalho</td>
              <td style={{ padding: "4px 12px", textAlign: "right", color: "#a04040" }} data-testid="rec-total-retrabalho">− {brl(data.total_retrabalho)}</td>
            </tr>
          )}
          <tr style={{ borderTop: "1.5px solid #1a1a1a" }}>
            <td style={{ padding: "8px 12px", fontWeight: 600, fontSize: 14 }}>Saldo a pagar</td>
            <td style={{ padding: "8px 12px", textAlign: "right", fontWeight: 600, fontSize: 18, fontFamily: "'Fraunces', Georgia, serif" }}>{brl(data.saldo)}</td>
          </tr>
          <tr>
            <td style={{ padding: "4px 12px", color: "#666" }}>Forma de pagamento</td>
            <td style={{ padding: "4px 12px", textAlign: "right" }}>{pagamento}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function ReciboPagamentosParciais({ recibo }) {
  const pagamentos = recibo?.pagamentos || [];
  const totalPago = recibo?.total_pago || 0;
  const totalPendente = recibo?.total_pendente || 0;
  const saldo = recibo?.snapshot?.saldo || 0;
  const saldoRest = recibo?.saldo_restante ?? saldo;
  const quitado = recibo?.quitado === true;
  const sobra = saldoRest < 0;
  if (pagamentos.length === 0) return null;
  const pubBadge = (s) => {
    const isPago = (s || "pago") === "pago";
    return {
      fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase",
      color: isPago ? "#3f7a4a" : "#a58122",
      border: `1px solid ${isPago ? "#3f7a4a" : "#a58122"}`,
      padding: "2px 8px", borderRadius: 2,
      background: "#fff",
      display: "inline-block",
    };
  };
  return (
    <div style={{ marginBottom: 22, padding: "16px 18px", background: "#f7f5ee", border: "1px solid #d9d3c0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "#666" }}>
          Pagamentos
        </div>
        {quitado && (
          <span style={{
            fontSize: 10, letterSpacing: 2, textTransform: "uppercase",
            color: "#3f7a4a", border: "1.5px solid #3f7a4a",
            padding: "3px 12px", borderRadius: 2, background: "#fff",
          }} data-testid="badge-recibo-quitado">
            QUITADO
          </span>
        )}
      </div>
      <div className="rec-scroll">
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ color: "#666", fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase" }}>
            <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid #ccc" }}>Data</th>
            <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid #ccc" }}>Forma</th>
            <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid #ccc" }}>Observações</th>
            <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid #ccc" }}>Status</th>
            <th style={{ textAlign: "right", padding: "6px 8px", borderBottom: "1px solid #ccc" }}>Valor</th>
          </tr>
        </thead>
        <tbody>
          {pagamentos.map(p => {
            const isPago = (p.status || "pago") === "pago";
            return (
            <tr key={p.id} style={{ borderBottom: "1px solid #eaeaea" }} data-testid={`pub-pagamento-${p.id}`}>
              <td style={{ padding: "6px 8px" }}>{fmtDataBR(p.data)}</td>
              <td style={{ padding: "6px 8px", color: "#555" }}>{p.forma_pagamento || "—"}</td>
              <td style={{ padding: "6px 8px", color: "#666" }}>{p.observacoes || ""}</td>
              <td style={{ padding: "6px 8px" }}>
                <span style={pubBadge(p.status)} data-testid={`pub-pagto-status-${p.id}`}>
                  {isPago ? "PAGO" : "PENDENTE"}
                </span>
              </td>
              <td style={{
                padding: "6px 8px", textAlign: "right", fontWeight: 500,
                color: isPago ? "#3f7a4a" : "#a58122",
              }}>{brl(p.valor)}</td>
            </tr>
          );})}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: "1.5px solid #1a1a1a" }}>
            <td colSpan={4} style={{ padding: "8px", fontWeight: 500 }}>Total pago</td>
            <td style={{ padding: "8px", textAlign: "right", fontWeight: 600, color: "#3f7a4a" }}>{brl(totalPago)}</td>
          </tr>
          {totalPendente > 0 && (
            <tr>
              <td colSpan={4} style={{ padding: "4px 8px", color: "#a58122", fontSize: 11 }}>
                Pendente (previsão, ainda não recebido)
              </td>
              <td style={{ padding: "4px 8px", textAlign: "right", color: "#a58122", fontSize: 11 }}>
                {brl(totalPendente)}
              </td>
            </tr>
          )}
          <tr>
            <td colSpan={4} style={{ padding: "6px 8px", color: "#666" }}>
              {sobra ? "Sobra sobre o saldo" : "Saldo restante"}
            </td>
            <td style={{
              padding: "6px 8px", textAlign: "right", fontWeight: 600,
              color: quitado ? "#3f7a4a" : (sobra ? "#c9a144" : "#a04040"),
              fontSize: 14,
            }}
              data-testid="pub-saldo-restante"
            >
              {sobra ? brl(-saldoRest) : brl(saldoRest)}
            </td>
          </tr>
        </tfoot>
      </table>
      </div>
    </div>
  );
}

function ReciboTextoLegal({ tipoDoc }) {
  const textos = {
    colocacao: 'Ao assinar este documento, o prestador de serviços declara que os serviços discriminados acima foram integralmente executados de forma satisfatória, pelo valor bruto total constante neste recibo, do qual foram deduzidos os adiantamentos já recebidos, dando plena QUITAÇÃO quanto ao saldo líquido ora pago. O presente instrumento tem força de Recibo de Prestação de Serviços Autônomos, nos termos dos arts. 593-609 da Lei n.º 10.406/2002 (Código Civil), ficando expressamente consignada a ausência de vínculo empregatício, nos termos do art. 4.º da Lei n.º 6.019/1974 e art. 443 da CLT.',
    frete: 'Ao assinar este documento, o prestador declara que o(s) frete(s) discriminados acima foram integralmente executados de forma satisfatória, pelo valor bruto constante neste recibo, do qual foram deduzidos os adiantamentos já recebidos, dando plena QUITAÇÃO quanto ao saldo líquido ora pago. O presente instrumento tem força de Recibo de Frete, nos termos da Lei n.º 11.442/2007, ficando expressamente consignada a ausência de vínculo empregatício, nos termos do art. 4.º da Lei n.º 6.019/1974 e art. 443 da CLT.',
    geral: 'Ao assinar este documento, o prestador de serviços declara que os serviços discriminados acima foram integralmente executados de forma satisfatória, pelo valor bruto constante neste recibo, do qual foram deduzidos os adiantamentos já recebidos, dando plena QUITAÇÃO quanto ao saldo líquido ora pago. O presente instrumento tem força de Recibo de Prestação de Serviços Autônomos, ficando expressamente consignada a ausência de vínculo empregatício, nos termos do art. 4.º da Lei n.º 6.019/1974 e art. 443 da CLT.',
  };
  return (
    <div style={{ fontSize: 11, color: "#333", lineHeight: 1.7, textAlign: "justify", marginBottom: 40, borderTop: "1px solid #ddd", paddingTop: 16 }}>
      {textos[tipoDoc] || textos.geral}
    </div>
  );
}

function ReciboAssinatura({ data }) {
  const hoje = new Date().toLocaleDateString("pt-BR");
  return (
    <div style={{ marginTop: 60 }}>
      <div style={{ textAlign: "center", marginBottom: 30, fontSize: 12, color: "#555" }}>
        {data.obra?.cidade || "________________"}, {hoje}
      </div>
      <div className="rec-assin-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40 }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ borderTop: "1px solid #1a1a1a", paddingTop: 6, fontSize: 12 }}>
            <strong>{data.prestador?.nome || "—"}</strong>
            <div style={{ color: "#666", fontSize: 11, marginTop: 2 }}>CPF: {data.prestador?.cpf || "________________"}</div>
            <div style={{ color: "#888", fontSize: 10, marginTop: 4, letterSpacing: 1 }}>PRESTADOR</div>
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ borderTop: "1px solid #1a1a1a", paddingTop: 6, fontSize: 12 }}>
            <strong>CAMASA</strong>
            <div style={{ color: "#666", fontSize: 11, marginTop: 2 }}>&nbsp;</div>
            <div style={{ color: "#888", fontSize: 10, marginTop: 4, letterSpacing: 1 }}>CONTRATANTE</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
 * ReciboPublicView + SignaturePad
 * Rota PÚBLICA (sem auth): /r/:token
 * Prestador abre no celular, confere itens e assina digitalmente (canvas).
 * ============================================================ */
export function ReciboPublicView() {
  const { token } = useParams();
  const [recibo, setRecibo] = useState(null);
  const [erro, setErro] = useState(null);
  const [assinando, setAssinando] = useState(false);
  const [nome, setNome] = useState("");
  const [cpf, setCpf] = useState("");
  const [assinaturaB64, setAssinaturaB64] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/recibo/${token}`)
      .then(r => {
        setRecibo(r.data);
        const p = r.data.snapshot?.prestador || {};
        setNome(r.data.assinado_nome || p.nome || "");
        setCpf(r.data.assinado_cpf || p.cpf || "");
      })
      .catch(e => setErro(e?.response?.data?.detail || e.message));
  }, [token]);

  const submeter = async () => {
    if (!assinaturaB64) { toastError("Desenhe sua assinatura no quadro abaixo"); return; }
    if (nome.trim().length < 3) { toastError("Informe seu nome completo"); return; }
    setAssinando(true);
    try {
      await axios.post(`${API}/public/recibo/${token}/assinar`, {
        assinatura_data_url: assinaturaB64,
        nome: nome.trim(),
        cpf: cpf.trim() || null,
      });
      const r = await axios.get(`${API}/public/recibo/${token}`);
      setRecibo(r.data);
      toastSuccess("Recibo assinado com sucesso!", "A CAMASA já foi notificada. Você pode imprimir uma cópia se quiser.");
    } catch (e) {
      toastError("Não conseguimos registrar sua assinatura", { description: pickErrorMsg(e), onRetry: submeter });
    } finally {
      setAssinando(false);
    }
  };

  if (erro) return (
    <div style={{ minHeight: "100vh", background: "#f5efe0", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", padding: 40, maxWidth: 480, textAlign: "center", fontFamily: "Georgia, serif" }}>
        <h2 style={{ color: "#a04040", fontFamily: "'Fraunces', Georgia, serif", fontWeight: 400 }}>Recibo indisponível</h2>
        <p style={{ color: "#666" }}>{erro}</p>
      </div>
    </div>
  );
  if (!recibo) return <div style={{ padding: 40, color: "#666", textAlign: "center" }}>Carregando recibo…</div>;

  const snap = recibo.snapshot || {};
  const assinado = recibo.status === "assinado";
  const tipoDoc = decideTipoRecibo(snap.tipos);

  return (
    <>
      <style>{`
        @page { size: A4; margin: 15mm 12mm; }
        body { background: #f0ece2; }
        .rec-noprint { display: block; }
        @media print {
          html, body, #root, .app { background: #ffffff !important; }
          .rec-noprint { display: none !important; }
          .rec-doc { box-shadow: none !important; margin: 0 !important; max-width: none !important; }
        }
        ${RECIBO_MOBILE_CSS}
      `}</style>

      <div className="rec-noprint rec-topbar" style={{ background: "#1a1820", padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, zIndex: 10 }}>
        <div style={{ color: ink2, fontSize: 13, fontFamily: "'Fraunces',serif" }}>
          CAMASA · Recibo do Prestador
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <span style={{ fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase", color: assinado ? "#5fa86c" : gold, border: `1px solid ${assinado ? "#5fa86c" : gold}`, padding: "3px 10px", borderRadius: 2 }} data-testid="recibo-status">
            {assinado ? "ASSINADO" : "PENDENTE"}
          </span>
          {assinado && <button onClick={() => window.print()} style={btnPrimary} data-testid="btn-print-publico">Imprimir / Salvar PDF</button>}
        </div>
      </div>

      <div className="rec-doc" style={{
        maxWidth: 800, margin: "24px auto", background: "#fff", color: "#1a1a1a",
        fontFamily: "Georgia, 'Times New Roman', serif", padding: "40px 44px",
        boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
      }}>
        <ReciboHeader tipoDoc={tipoDoc} data={snap} />
        <ReciboPartes data={snap} />
        <ReciboTabela servicos={snap.servicos || []} />
        <ReciboAdiantamentos adiantamentos={snap.adiantamentos || []} />
        <ReciboRetrabalhos retrabalhos={snap.retrabalhos} />
        <ReciboTotais data={snap} pagamento={recibo.forma_pagamento || "PIX"} />
        <ReciboPagamentosParciais recibo={recibo} />
        <ReciboTextoLegal tipoDoc={tipoDoc} />

        {assinado ? (
          <AssinaturaAposto recibo={recibo} snap={snap} />
        ) : (
          <BlocoAssinaturaPendente
            snap={snap}
            nome={nome} setNome={setNome}
            cpf={cpf} setCpf={setCpf}
            onDraw={setAssinaturaB64}
            onSubmit={submeter}
            enviando={assinando}
          />
        )}
      </div>
    </>
  );
}

function BlocoAssinaturaPendente({ snap, nome, setNome, cpf, setCpf, onDraw, onSubmit, enviando }) {
  return (
    <div style={{ marginTop: 20, padding: 20, background: "#fafaf7", border: "1px solid #ddd" }} data-testid="bloco-assinatura">
      <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "#666", marginBottom: 14 }}>
        Assinatura digital do prestador
      </div>
      <div className="rec-form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
        <div>
          <label style={{ fontSize: 11, color: "#666", display: "block", marginBottom: 4 }}>Nome completo</label>
          <input value={nome} onChange={e => setNome(e.target.value)} style={inpPub} data-testid="input-nome" placeholder="Digite seu nome completo" />
        </div>
        <div>
          <label style={{ fontSize: 11, color: "#666", display: "block", marginBottom: 4 }}>CPF (opcional)</label>
          <input value={cpf} onChange={e => setCpf(e.target.value)} style={inpPub} data-testid="input-cpf" placeholder="000.000.000-00" />
        </div>
      </div>

      <div style={{ fontSize: 11, color: "#666", marginBottom: 6 }}>Desenhe sua assinatura abaixo:</div>
      <SignaturePad onChange={onDraw} />

      <div style={{ marginTop: 14, fontSize: 11, color: "#555", lineHeight: 1.6, textAlign: "justify" }}>
        Ao clicar em <strong>&quot;Confirmar e Assinar&quot;</strong>, declaro que os serviços discriminados acima foram integralmente executados de forma satisfatória e dou plena quitação do saldo líquido de <strong>{brl(snap.saldo)}</strong>.
      </div>

      <div style={{ marginTop: 18, display: "flex", justifyContent: "flex-end" }}>
        <button
          onClick={onSubmit}
          disabled={enviando}
          style={{
            background: "#1a1a1a", color: "#fff", border: "none",
            padding: "12px 28px", fontSize: 13, fontWeight: 600, letterSpacing: 1,
            cursor: enviando ? "wait" : "pointer", textTransform: "uppercase",
          }}
          data-testid="btn-confirmar-assinatura"
        >
          {enviando ? "Enviando…" : "Confirmar e Assinar"}
        </button>
      </div>
    </div>
  );
}

function AssinaturaAposto({ recibo, snap }) {
  const dataHora = recibo.assinado_em
    ? new Date(recibo.assinado_em).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })
    : "—";
  return (
    <div style={{ marginTop: 40 }} data-testid="bloco-assinado">
      <div style={{ textAlign: "center", marginBottom: 20, fontSize: 12, color: "#555" }}>
        {snap.obra?.cidade || "________________"}, {dataHora}
      </div>
      <div className="rec-assin-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40 }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ height: 90, display: "flex", alignItems: "flex-end", justifyContent: "center", marginBottom: 6 }}>
            {recibo.assinatura_data_url && (
              <img src={recibo.assinatura_data_url} alt="Assinatura" style={{ maxHeight: 84, maxWidth: "90%" }} />
            )}
          </div>
          <div style={{ borderTop: "1px solid #1a1a1a", paddingTop: 6, fontSize: 12 }}>
            <strong>{recibo.assinado_nome || snap.prestador?.nome || "—"}</strong>
            <div style={{ color: "#666", fontSize: 11, marginTop: 2 }}>CPF: {recibo.assinado_cpf || snap.prestador?.cpf || "—"}</div>
            <div style={{ color: "#888", fontSize: 10, marginTop: 4, letterSpacing: 1 }}>PRESTADOR · assinado digitalmente</div>
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ height: 90 }} />
          <div style={{ borderTop: "1px solid #1a1a1a", paddingTop: 6, fontSize: 12 }}>
            <strong>CAMASA</strong>
            <div style={{ color: "#666", fontSize: 11, marginTop: 2 }}>&nbsp;</div>
            <div style={{ color: "#888", fontSize: 10, marginTop: 4, letterSpacing: 1 }}>CONTRATANTE</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* --- SignaturePad: canvas HTML5 puro, mouse + touch --- */
function SignaturePad({ onChange }) {
  const canvasRef = React.useRef(null);
  const drawingRef = React.useRef(false);
  const lastRef = React.useRef({ x: 0, y: 0 });
  const hasStrokeRef = React.useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#1a1a1a";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = getPos(e);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const p = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    hasStrokeRef.current = true;
  };
  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (hasStrokeRef.current) {
      onChange(canvasRef.current.toDataURL("image/png"));
    }
  };
  const limpar = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    hasStrokeRef.current = false;
    onChange(null);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        style={{ width: "100%", height: 160, background: "#fff", border: "1.5px dashed #999", cursor: "crosshair", touchAction: "none", display: "block" }}
        data-testid="signature-canvas"
      />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
        <span style={{ fontSize: 10, color: "#888" }}>Use o dedo (celular) ou o mouse para desenhar</span>
        <button onClick={limpar} style={{ background: "transparent", border: "1px solid #999", color: "#555", padding: "4px 10px", fontSize: 11, cursor: "pointer" }} data-testid="btn-limpar-assinatura">
          Limpar
        </button>
      </div>
    </div>
  );
}

const inpPub = {
  width: "100%", background: "#fff", border: "1px solid #ccc",
  color: "#1a1a1a", padding: "8px 10px", fontSize: 13, boxSizing: "border-box",
  fontFamily: "Georgia, serif",
};

